import { computed, nextTick, ref, watch } from 'vue'
import type { Ref } from 'vue'

import { getMentionQuery } from '@/helpers/mention'
import type { ChatbotEntity } from '@/services/chatbot-service/models'

/**
 * Handles @mention detection, filtering, keyboard navigation and entity insertion
 * inside a textarea.
 *
 * @param draft       - Ref bound to the textarea value (v-model)
 * @param entities    - Ref<ChatbotEntity[]> list to search in
 * @param getTextarea - returns the native HTMLTextAreaElement (needed for cursor ops)
 */
export function useMention(
  draft: Ref<string>,
  entities: Ref<ChatbotEntity[]>,
  getTextarea: () => HTMLTextAreaElement | null,
) {
  const activeIndex = ref(0)
  const mentionMatch = ref<{ query: string; start: number } | null>(null)
  const isInserting = ref(false)

  const filteredEntities = computed<ChatbotEntity[]>(() => {
    if (!mentionMatch.value) return []
    const q = mentionMatch.value.query.toLowerCase()
    return entities.value.filter(
      (e) => e.name.toLowerCase().includes(q) || e.id.toLowerCase().includes(q),
    )
  })

  const showDropdown = computed(() => filteredEntities.value.length > 0)

  // Watch draft so detection works regardless of how Vuetify forwards input events.
  // Skip when selectEntity is mid-insertion to avoid reopening the dropdown.
  watch(draft, () => {
    if (isInserting.value) return
    const textarea = getTextarea()
    const cursorPos = textarea?.selectionStart ?? draft.value.length
    mentionMatch.value = getMentionQuery(draft.value, cursorPos)
    activeIndex.value = 0
  })

  // Called on click and keyup so any cursor repositioning updates the active mention match.
  // ArrowUp/ArrowDown are skipped when the dropdown is open — they drive dropdown navigation,
  // not cursor movement (cursor doesn't move because onKeydown calls preventDefault).
  function onCursorMove(e?: KeyboardEvent) {
    if (isInserting.value) return
    if (e && showDropdown.value && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) return
    const textarea = getTextarea()
    if (!textarea) return
    mentionMatch.value = getMentionQuery(draft.value, textarea.selectionStart)
    activeIndex.value = 0
  }

  /**
   * Handles mention-related keys. Returns true when the event was consumed
   * so the caller can skip its own handling (e.g. send on Enter).
   */
  function onKeydown(e: KeyboardEvent): boolean {
    if (!showDropdown.value) return false

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        activeIndex.value = (activeIndex.value + 1) % filteredEntities.value.length
        return true

      case 'ArrowUp':
        e.preventDefault()
        activeIndex.value = (activeIndex.value - 1 + filteredEntities.value.length) % filteredEntities.value.length
        return true

      case 'Enter':
      case 'Tab': {
        const entity = filteredEntities.value[activeIndex.value]
        if (!entity) return false
        e.preventDefault()
        selectEntity(entity)
        return true
      }

      case 'Escape':
        mentionMatch.value = null
        return true

      default:
        return false
    }
  }

  function selectEntity(entity: ChatbotEntity) {
    if (!mentionMatch.value) return
    const textarea = getTextarea()
    const cursorPos = textarea?.selectionStart ?? draft.value.length
    const { start } = mentionMatch.value

    const before = draft.value.slice(0, start)
    const after = draft.value.slice(cursorPos)
    const insertion = `@${entity.name} `

    isInserting.value = true
    mentionMatch.value = null
    activeIndex.value = 0
    draft.value = before + insertion + after

    nextTick(() => {
      if (textarea) {
        const newPos = start + insertion.length
        textarea.focus()
        textarea.setSelectionRange(newPos, newPos)
      }
      isInserting.value = false
    })
  }

  function close() {
    mentionMatch.value = null
    activeIndex.value = 0
  }

  return {
    showDropdown,
    filteredEntities,
    activeIndex,
    onCursorMove,
    onKeydown,
    selectEntity,
    close,
  }
}
